import { useEffect } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import { useOrg } from '../org/useOrg';
import { Spinner } from './Spinner';

/**
 * Wraps every `/w/:slug/…` route. The URL slug is the source of truth for the
 * active workspace: resolve it from the user's memberships, make it active
 * (drives the `X-Org-Id` interceptor), and only render the page once the active
 * workspace matches the URL — so child pages never fetch under the wrong org.
 *
 * A slug that isn't one of the user's workspaces (renamed away, never existed,
 * or someone else's) shows a privacy-safe "not found / not a member" panel — it
 * does not reveal whether the workspace exists.
 */
export function WorkspaceLayout() {
  const { slug = '' } = useParams();
  const { orgs, activeOrg, setActiveOrg, isLoading } = useOrg();
  const target = orgs.find((o) => o.slug === slug) ?? null;

  useEffect(() => {
    if (target && activeOrg?.id !== target.id) {
      setActiveOrg(target.id);
    }
  }, [target, activeOrg?.id, setActiveOrg]);

  if (isLoading) {
    return (
      <div className="grid min-h-[40vh] place-items-center">
        <Spinner />
      </div>
    );
  }

  if (!target) {
    const fallback = orgs[0]?.slug;
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h1 className="text-lg font-semibold text-ink">Workspace not found</h1>
        <p className="mt-2 text-sm text-ink-muted">
          This workspace URL doesn’t exist or you’re not a member of it.
        </p>
        {fallback && (
          <Link to={`/w/${fallback}`} className="btn-primary mt-4 inline-block">
            Go to your workspace
          </Link>
        )}
      </div>
    );
  }

  // Active org lags the URL by one render (setActiveOrg is an effect) — hold the
  // page until they agree so the first request carries the right X-Org-Id.
  if (activeOrg?.id !== target.id) {
    return (
      <div className="grid min-h-[40vh] place-items-center">
        <Spinner />
      </div>
    );
  }

  // The global nav (Overview/Projects/Prototypes) already links back to the
  // cross-workspace lists, and each page's own header names its context, so no
  // per-page workspace strip is needed here.
  return <Outlet />;
}
